/**
 * Customer satisfaction surveys (tracker item 69). Mirrors the backend
 * modules/surveys/surveys.types.ts API shapes.
 */
import type { PaginationInfo } from './types';

export type SurveyMode = 'OFF' | 'ON';
export type SurveyRequestStatus = 'PENDING' | 'SENT' | 'ANSWERED' | 'EXPIRED' | 'CANCELLED';
export type PublicSurveyStatus = 'OPEN' | 'ANSWERED' | 'EXPIRED';

export interface SurveyScale {
  min: number;
  max: number;
  labels: Record<number, string>;
}

/** GET/PUT /surveys/settings */
export interface SurveySettings {
  mode: SurveyMode;
  delayHours: number;
  lowScoreThreshold: number;
  customerCooldownDays: number;
  commentPrompt: string | null;
  /** YYYY-MM-DD: jobs completed before it are never surveyed automatically */
  activeSince: string | null;
  /** The company's notifications master switch, and the survey message's own switch */
  notificationsEnabled: boolean;
  eventActive: boolean;
  scale: SurveyScale;
}

export interface UpdateSurveySettingsInput {
  mode?: SurveyMode;
  delayHours?: number;
  lowScoreThreshold?: number;
  customerCooldownDays?: number;
  /** '' or null clears it */
  commentPrompt?: string | null;
}

export interface SurveyResponseItem {
  id: string;
  requestId: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  lowScore: boolean;
  job: { id: string; scheduledDate: string | null; completedDate: string | null };
  customer: { id: string; name: string };
  technician: { id: string; name: string } | null;
}

export interface SurveyListQuery {
  startDate?: string;
  endDate?: string;
  technicianId?: string;
  rating?: number;
  maxRating?: number;
  lowOnly?: boolean;
  jobId?: string;
  customerId?: string;
  page?: number;
  limit?: number;
}

export interface SurveyListResponse {
  data: SurveyResponseItem[];
  pagination: PaginationInfo;
  /** A score at or below this is "low" */
  threshold: number;
}

export interface SurveyRequestSummary {
  id: string;
  status: SurveyRequestStatus;
  scheduledFor: string;
  sentAt: string | null;
  expiresAt: string | null;
  sendCount: number;
  answeredAt: string | null;
  cancelReason: string | null;
  cancelReasonText: string | null;
}

/** GET /jobs/:id/survey */
export interface JobSurvey {
  jobId: string;
  enabled: boolean;
  canSend: boolean;
  unavailableReason: string | null;
  wouldSkip: string | null;
  wouldSkipText: string | null;
  automatic: { eligible: boolean; reason: string | null };
  request: SurveyRequestSummary | null;
  response: { rating: number; comment: string | null; createdAt: string; lowScore: boolean } | null;
}

/** POST /jobs/:id/survey/send */
export interface SendSurveyResult {
  request: SurveyRequestSummary;
  message: { id: string; status: string; skipReason: string | null; skipReasonText: string | null };
  deduplicated: boolean;
}

/** GET /public/surveys/:token (no sign-in) */
export interface PublicSurvey {
  status: PublicSurveyStatus;
  companyName: string;
  customerFirstName: string;
  jobDate: string | null;
  serviceList: string;
  technicianFirstName: string | null;
  commentPrompt: string;
  scale: SurveyScale;
  commentMaxLength: number;
  /** Only once answered */
  rating: number | null;
}

export interface PublicSurveyAnswer {
  rating: number;
  comment?: string | null;
}

export type SatisfactionGroupBy = 'week' | 'month';

export interface SatisfactionReportQuery {
  startDate?: string;
  endDate?: string;
  technicianId?: string;
  groupBy?: SatisfactionGroupBy;
}

/** GET /reports/customer-satisfaction */
export interface SatisfactionReport {
  startDate: string;
  endDate: string;
  groupBy: SatisfactionGroupBy;
  threshold: number;
  summary: {
    requestsSent: number;
    requestsAnswered: number;
    /** 0 to 1; null when nothing was sent */
    responseRate: number | null;
    responses: number;
    averageRating: number | null;
    lowScoreCount: number;
  };
  distribution: { rating: number; label: string; count: number; percent: number }[];
  trend: { periodStart: string; label: string; responses: number; averageRating: number }[];
  byTechnician: { technicianId: string | null; name: string; responses: number; averageRating: number; lowScoreCount: number }[];
}

/** Backend error codes of the survey routes. */
export const SURVEY_ERROR_CODES = {
  alreadyAnswered: 'SURVEY_ALREADY_ANSWERED',
  expired: 'SURVEY_EXPIRED',
  notificationOff: 'NOTIFICATION_OFF',
  jobNotCompleted: 'JOB_NOT_COMPLETED',
} as const;
